import LoadingBox from 'components/Common/Loading/LoadingBox'
import NumberRating from 'components/Common/NumberRating'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { useMemo } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import HotelDetailAmenities from './HotelDetailAmenities'
import HotelDetailInclusions from './HotelDetailInclusions'
import HotelDetailPricing from './HotelDetailPricing'
import { ContentWrapper } from './HotelListingDrawer'
import { OfferDetailImageGallery, OfferDetailDescription, OfferDetailReviews, OfferDetailMap } from '../OfferDetail'
import type { OfferDetailReview } from '../OfferDetail'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import type { TripItineraryData, TripStop } from 'tripPlanner/api/types'
import type { OfferClickSearchParams } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/ChatMessageActionsContext'
import type { DrawerHotel } from './types'
import BookedLabel from 'tripPlanner/components/BookedLabel'

const HeaderGroup = styled(Group)`
  flex: 1;
  min-width: 0;
`

interface Props {
  hotel: DrawerHotel
  itineraryData?: TripItineraryData
  contextStop?: TripStop
  onSendMessage?: (message: string) => void
  searchParams?: OfferClickSearchParams
  reviews?: Array<OfferDetailReview>
  reviewsLoading?: boolean
  isBooked?: boolean
  orderId?: string
}

function HotelDetailView({ hotel, itineraryData, contextStop, onSendMessage, searchParams, reviews, reviewsLoading, isBooked, orderId }: Props) {
  const {
    name,
    location,
    description,
    images,
    rating,
    reviewsTotal,
    inclusions,
    amenities,
    price,
    value,
    discountPercent,
    offerId,
    offerType,
    deeplinkUrl,
    geoLocation,
    saleUnit,
  } = hotel

  const hasDescription = !!description
  const hasAmenities = !!amenities && amenities.length > 0
  const hasInclusions = !!inclusions && inclusions.length > 0
  const hasReviews = !!reviews && reviews.length > 0
  const hasGeoLocation = !!geoLocation

  const filterNights = useMemo(() => {
    if (!searchParams?.checkIn || !searchParams?.checkOut) return undefined
    const nights = moment(searchParams.checkOut).diff(moment(searchParams.checkIn), 'days')
    return nights > 0 ? nights : undefined
  }, [searchParams])

  return (
    <ContentWrapper>
      <Group direction="vertical" gap={24}>
        <Group
          direction="horizontal"
          gap={16}
          horizontalAlign="space-between"
          verticalAlign="start"
        >
          <HeaderGroup direction="vertical" gap={12}>
            <Group direction="vertical">
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <Heading variant="heading2">{name}</Heading>
                {isBooked && <BookedLabel />}
              </Group>
              <BodyText variant="medium" colour="neutral-two">{location}</BodyText>
            </Group>
            {rating !== undefined && rating > 0 && (
              <NumberRating
                rating={rating}
                total={reviewsTotal}
                variant="small"
                squareContainer
              />
            )}
          </HeaderGroup>
          {onSendMessage && !isBooked && (
            <AddHotelToTripButton
              itineraryData={itineraryData}
              hotelName={name}
              offerId={offerId}
              offerType={offerType}
              hotelCity={location}
              hotelCoordinates={geoLocation}
              contextStop={contextStop}
              onSendMessage={onSendMessage}
              variant="outline"
              size="small"
              filterNights={filterNights}
            />
          )}
        </Group>
        <OfferDetailImageGallery images={images} />

        {hasDescription && (
          <OfferDetailDescription description={description} />
        )}

        <Divider kind="secondary" />

        <HotelDetailPricing
          price={price}
          value={value}
          discountPercent={discountPercent}
          offerId={offerId}
          deeplinkUrl={deeplinkUrl}
          saleUnit={saleUnit}
          isBooked={isBooked}
          orderId={orderId}
        />

        {hasAmenities && (
          <>
            <Divider kind="secondary" />
            <HotelDetailAmenities amenities={amenities} />
          </>
        )}

        {hasInclusions && (
          <>
            <Divider kind="secondary" />
            <HotelDetailInclusions inclusions={inclusions} />
          </>
        )}

        {(hasReviews || reviewsLoading) && (
          <>
            <Divider kind="secondary" />
            {reviewsLoading && !hasReviews && (
              <Group direction="vertical" gap={12}>
                <LoadingBox height={20} width={120} borderRadius="S" />
                <LoadingBox height={60} width={320} borderRadius="S" />
              </Group>
            )}
            {hasReviews && (
              <OfferDetailReviews
                rating={rating}
                reviewsTotal={reviewsTotal}
                reviews={reviews}
              />
            )}
          </>
        )}

        {hasGeoLocation && (
          <>
            <Divider kind="secondary" />
            <OfferDetailMap
              geoLocation={geoLocation}
              offerName={name}
            />
          </>
        )}
      </Group>
    </ContentWrapper>
  )
}

export default HotelDetailView
